import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { Spacer } from 'common/src/designSystem/components/spacer';
import { Span } from 'common/src/designSystem/components/span';
import { SegmentsFolderId, SegmentsFoldersSegmentsFragment } from 'common/src/generated/types';
import { isNonEmptyArray } from 'common/src/util/array';
import { isNonEmptyString } from 'common/src/util/string';
import * as React from 'react';
import { Button } from '../../designSystem/components/button';
import { Modal } from '../../designSystem/components/modal/modal';
import { RadioText } from '../../designSystem/components/radio';
import { RichSelect } from '../../designSystem/components/richSelect/richSelect';
import { Textarea } from '../../designSystem/components/textarea';
import { TextInput } from '../../designSystem/components/textInput';
import { useSegmentCreateMutation, useSegmentUpdateMutation } from '../../generated/graphqlHooks';
import { useHeavent } from '../../hooks/useHeavent';
import { useSegmentsContext } from '../segmentsContext';

interface ISaveSegmentProps {
    segmentsFolders: SegmentsFoldersSegmentsFragment[];
}

export const SaveSegment = (props: ISaveSegmentProps) => {
    const { history, translate } = useHeavent();
    const {
        columns,
        columnsExport,
        columnsSearch,
        eventId,
        getSegmentPath,
        organizationId,
        predicates,
        reload,
        rendering,
        segment,
        segmentId,
        segmentType,
        sort,
        setIsSaveSegmentVisible
    } = useSegmentsContext();
    const onClose = React.useCallback(() => {
        setIsSaveSegmentVisible(false);
    }, [setIsSaveSegmentVisible]);
    const { mutate: segmentUpdate, isLoading: isSegmentUpdateLoading } = useSegmentUpdateMutation();
    const { mutate: segmentCreate, isLoading: isSegmentCreateLoading } = useSegmentCreateMutation();
    const [newSegmentSelected, setNewSegmentSelected] = React.useState(false);
    const [name, setName] = React.useState('');
    const [segmentFolderId, setSegmentFolderId] = React.useState(
        props.segmentsFolders.find((sf) => sf.segments.find((s) => s.id === segmentId))?.id ??
            props.segmentsFolders[0].id
    );
    const [description, setDescription] = React.useState(segment.description || undefined);
    const valid = newSegmentSelected ? isNonEmptyString(name) : true;

    return (
        <Modal.Root isOpen={true} onClose={onClose}>
            <Modal.Portal>
                <Modal.Header icon="floppy-disk" subtitle={translate('sauvegarder_vos_15289')}>
                    {translate('enregistrer_une_52986')}
                </Modal.Header>

                <Modal.Content>
                    <Box color="gray800" fontWeight="medium">
                        {translate('que_souhaitez_v_33936')}
                    </Box>

                    <Spacer height="2" />

                    <Flex direction="column" gap="3">
                        <RadioText
                            css={{
                                [`& label > div > div`]: {
                                    fontWeight: '$regular'
                                }
                            }}
                            state={newSegmentSelected ? 'unchecked' : 'checked'}
                            onClick={() => {
                                setNewSegmentSelected(false);
                            }}
                        >
                            {translate('enregistrer_les_31603')
                                .split('{1}')
                                .map((_, i) =>
                                    i === 1 ? (
                                        <React.Fragment key={i}>
                                            <Span fontWeight="medium">{segment.name}</Span>
                                            {_}
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment key={i}>{_}</React.Fragment>
                                    )
                                )}
                        </RadioText>

                        <Flex direction="column" gap="2">
                            <RadioText
                                css={{
                                    [`& label > div > div`]: {
                                        fontWeight: '$regular'
                                    }
                                }}
                                state={newSegmentSelected ? 'checked' : 'unchecked'}
                                onClick={() => {
                                    setNewSegmentSelected(true);
                                }}
                            >
                                {translate('cr_er_une_nouve_37002')}
                            </RadioText>

                            {newSegmentSelected && (
                                <Box css={{ marginLeft: '$6' }}>
                                    <TextInput
                                        hint={translate('cette_vue_sera_83858')}
                                        placeholder={translate('nom_de_votre_no_21748')}
                                        value={name}
                                        onChange={setName}
                                    />
                                </Box>
                            )}
                        </Flex>
                    </Flex>

                    <Spacer height="4" />

                    <RichSelect
                        isSearchVisible={true}
                        label={translate('dans_quel_dossi_07643')}
                        shouldParseAsInt={true}
                        values={[segmentFolderId]}
                        onChange={(newSegmentFolderId) => {
                            if (isNonEmptyArray(newSegmentFolderId)) {
                                setSegmentFolderId(newSegmentFolderId[0] as SegmentsFolderId);
                            }
                        }}
                    >
                        {props.segmentsFolders.map((segmentFolder) => (
                            <option key={segmentFolder.id} value={segmentFolder.id}>
                                {segmentFolder.name}
                            </option>
                        ))}
                    </RichSelect>

                    <Spacer height="4" />

                    <Textarea
                        label={translate('Description')}
                        placeholder={translate('entrer_une_desc_74340')}
                        value={description}
                        onChange={setDescription}
                    />
                </Modal.Content>

                <Modal.Buttons>
                    {() => (
                        <Button
                            disabled={!valid}
                            isLoading={isSegmentCreateLoading || isSegmentUpdateLoading}
                            onClick={async () => {
                                const segment = {
                                    segmentType,
                                    name,
                                    description,
                                    predicates,
                                    columns,
                                    columnsExport,
                                    columnsSearch,
                                    sort,
                                    rendering
                                };

                                if (newSegmentSelected) {
                                    const {
                                        segmentCreate: { id }
                                    } = await segmentCreate({
                                        organizationId,
                                        eventId,
                                        segmentFolderId,
                                        segment
                                    });

                                    reload();
                                    history.push(getSegmentPath(id));
                                } else {
                                    await segmentUpdate({
                                        organizationId,
                                        eventId,
                                        segmentFolderId,
                                        segmentId,
                                        segment
                                    });

                                    reload();
                                }
                            }}
                        >
                            {translate('enregistrer_06519')}
                        </Button>
                    )}
                </Modal.Buttons>
            </Modal.Portal>
        </Modal.Root>
    );
};
